/* eslint-disable import/no-cycle */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Anchor, Button, Skeleton } from '@mantine/core';
import { ArrowRight, Heart, Shirt } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppStack from '../../../../common/AppStack';
import AppFlexbox from '../../../../common/AppFlexbox';
import {
  MEDIA_POSITION_ENUM,
  MEDIA_SIZE_ENUM
} from './wsProductInformationConfig';
import { getSelectedResourceBaseNavigationUrl } from '../../../../../helpers/webstoreHelper';
import WsProductMediaShowcase from './WsProductMediaShowcase';
import AppText from '../../../../common/AppText';
import {
  useCartController,
  useCartProductVariantSelector
} from '../../../../../helpers/checkout/useCartController';
import ProductCareInstructions from '../../ProductCareInstructions';
import { PRODUCT_CATEGORY_TYPE_ENUM } from '../../../../../config/productCategories';
import WsBlock from '../../blocks/WsBlock';
import { webstoreTrackingHelper } from '../../../../../helpers/webstoreTrackingHelper';

const WsProductInformationDisplay = ({
  config,
  themeConfig,
  ecomStoreProduct,
  ecomStoreCartProduct,
  ecomStoreCartPackage,
  colorSchemes,
  baseStoreUrl,
  onNavigate,
  ecomStoreTheme,
  mediaQueries,
  blocks,
  editModeEnabled,
  productNotFound,
  resourceNavigation,
  selectedResources,
  isPreview,
  isCartEdit,
  onEditCancel,
  onAddOrUpdateCallback
}) => {
  const { ecomStoreCart } = useCartController();
  const { isTabletOrSmaller } = mediaQueries;
  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const isSmall = config.mediaSize === MEDIA_SIZE_ENUM.SMALL;
  const isMedium = config.mediaSize === MEDIA_SIZE_ENUM.MEDIUM;
  const switchToVariantImageOnSelect = config.switchToVariantImage !== 'false';
  const baseResourceUrl = `${baseStoreUrl}${getSelectedResourceBaseNavigationUrl(
    resourceNavigation,
    'categories'
  )}`;
  const {
    formState,
    selectOptionInfo,
    onAddProductToCart,
    onUpdateCartProduct,
    onVariantChange,
    onInputAnswerChange,
    onDesignAnswerChange,
    onBundleProductVariantSelect,
    onQuantityChange
  } = useCartProductVariantSelector(ecomStoreProduct, {
    ecomStoreCartProduct,
    ecomStoreCartPackage,
    isCartEdit
  });

  const {
    storeProducts,
    storeProductOptions,
    productMedia,
    selectedVariants,
    selectedVariantsInfo,
    isBundleProduct,
    isExternalProduct,
    isUnavailable
  } = selectOptionInfo;

  const isButtonsDisabled = selectedVariantsInfo.some(
    (v) => v.outOfStock || v.unavailable
  );
  const hasVariantMedia = ecomStoreProduct?.ecomStoreProductVariants.some(
    (v) => !!v.ecomVendorProductVariant.previewImage
  );

  const enableImageNotAvailablePreview =
    config.enableImageNotAvailablePreview === 'true';

  useEffect(() => {
    if (ecomStoreProduct) {
      webstoreTrackingHelper.viewStoreProduct(
        ecomStoreProduct,
        ecomStoreTheme.ecomStore
      );
    }
  }, [ecomStoreProduct, ecomStoreTheme.ecomStore]);

  return productNotFound ? (
    <AppStack
      style={{
        gap: 0,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Shirt color={colorScheme.textColor} size="35%" />

      <AppStack style={{ gap: 20 }}>
        <AppText
          style={{
            fontSize: 22,
            fontWeight: 500,
            color: colorScheme.textColor
          }}
        >
          Product not found
        </AppText>
        <Button
          color={colorScheme.outlineButtonColor}
          component={Link}
          onClick={onNavigate}
          style={{ color: colorScheme.outlineButtonColor }}
          to={`${baseStoreUrl}/products`}
          variant="outline"
        >
          View all products
        </Button>
      </AppStack>
    </AppStack>
  ) : ecomStoreProduct && formState.initialized ? (
    <AppFlexbox
      style={{
        width: '100%',
        padding: '20px 0px',
        flexDirection: isTabletOrSmaller
          ? 'column'
          : config.desktopMediaPosition === MEDIA_POSITION_ENUM.LEFT
          ? 'row'
          : 'row-reverse',
        gap: isTabletOrSmaller ? 8 : 16
      }}
    >
      <AppStack
        style={{
          width: '100%',
          minWidth: 300,
          maxWidth: isTabletOrSmaller
            ? 'unset'
            : isSmall
            ? '45%'
            : isMedium
            ? '55%'
            : '65%',
          gap: 5
        }}
      >
        <WsProductMediaShowcase
          colorScheme={colorScheme}
          desktopMediaLayout={config.desktopMediaLayout}
          ecomVendorProductMedia={productMedia}
          editModeEnabled={editModeEnabled}
          enableImageNotAvailablePreview={
            !isBundleProduct &&
            hasVariantMedia &&
            enableImageNotAvailablePreview &&
            !selectedVariants[0]?.ecomVendorProductVariant.previewImage
          }
          hideBadge={isExternalProduct}
          isPreview={isPreview}
          mediaQueries={mediaQueries}
          mediaSize={config.mediaSize}
          selectedVariant={!isBundleProduct ? selectedVariants[0] : null}
          switchToVariantImage={switchToVariantImageOnSelect}
          themeConfig={themeConfig}
          thumbnailColumns={isSmall ? 4 : isMedium ? 5 : 6}
        />
      </AppStack>

      <AppStack
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (isCartEdit) {
            onUpdateCartProduct(onAddOrUpdateCallback);
          }
          else {
            onAddProductToCart(onAddOrUpdateCallback);
          }
        }}
        style={{
          width: '100%',
          maxWidth: isTabletOrSmaller
            ? 'unset'
            : isSmall
            ? '55%'
            : isMedium
            ? '45%'
            : '35%',
          marginRight:
            isTabletOrSmaller ||
            config.desktopMediaPosition === MEDIA_POSITION_ENUM.LEFT
              ? 0
              : 50,
          marginLeft:
            isTabletOrSmaller ||
            config.desktopMediaPosition !== MEDIA_POSITION_ENUM.LEFT
              ? 0
              : 50,
          gap: 5
        }}
      >
        <AppStack style={{ gap: 8 }}>
          {blocks.map((b) => (
            <WsBlock
              adjustments={formState.adjustments}
              baseResourceUrl={baseResourceUrl}
              baseStoreUrl={baseStoreUrl}
              colorScheme={colorScheme}
              ecomStoreCart={ecomStoreCart}
              ecomStoreProduct={ecomStoreProduct}
              ecomStoreProducts={storeProducts}
              ecomStoreTheme={ecomStoreTheme}
              editModeEnabled={editModeEnabled}
              enableImageNotAvailablePreview={enableImageNotAvailablePreview}
              entity={{
                title: ecomStoreProduct.ecomVendorProduct.name,
                description: ecomStoreProduct.ecomVendorProduct.description
              }}
              isBundleProduct={isBundleProduct}
              isCartEdit={isCartEdit}
              isExternalProduct={isExternalProduct}
              isUnavailable={isUnavailable}
              mediaQueries={mediaQueries}
              onAddToCart={onAddProductToCart}
              onBundleProductVariantSelect={onBundleProductVariantSelect}
              onDesignAnswerChange={onDesignAnswerChange}
              onInputChange={onInputAnswerChange}
              onNavigate={onNavigate}
              onQuantityChange={onQuantityChange}
              onVariantChange={onVariantChange}
              optionErrors={formState.optionErrors}
              quantity={formState.count}
              resourceNavigation={resourceNavigation}
              selectedProductVariantsState={formState.ecomStoreCartProducts}
              selectedResources={selectedResources}
              selectedVariants={selectedVariants}
              selectedVariantsInfo={selectedVariantsInfo}
              storeProductOptions={storeProductOptions}
              submitting={formState.loading}
              themeConfig={themeConfig}
              {...b}
            />
          ))}

          {!isCartEdit && isExternalProduct && (
            <AppStack>
              <Accordion variant="contained">
                <Accordion.Item value="care-instructions">
                  <Accordion.Control>
                    <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                      <Heart size={24} />
                      <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                        Care Instructions
                      </AppText>
                    </AppFlexbox>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <ProductCareInstructions
                      isSummaryView
                      showApparel={storeProducts.some(
                        (p) =>
                          p.ecomVendorProduct.fkEcomCategory ===
                            PRODUCT_CATEGORY_TYPE_ENUM.APPAREL &&
                          !p.ecomVendorProduct.productType
                            ?.toLowerCase()
                            .includes('hat')
                      )}
                      showDrinkware={storeProducts.some(
                        (p) =>
                          p.ecomVendorProduct.fkEcomCategory ===
                          PRODUCT_CATEGORY_TYPE_ENUM.DRINKWARE
                      )}
                      showHats={storeProducts.some(
                        (p) =>
                          p.ecomVendorProduct.fkEcomCategory ===
                            PRODUCT_CATEGORY_TYPE_ENUM.APPAREL &&
                          p.ecomVendorProduct.productType
                            ?.toLowerCase()
                            .includes('hat')
                      )}
                      showTotebags={storeProducts.some(
                        (p) =>
                          p.ecomVendorProduct.fkEcomCategory ===
                            PRODUCT_CATEGORY_TYPE_ENUM.MISC &&
                          p.ecomVendorProduct.productType
                            ?.toLowerCase()
                            .includes('bag')
                      )}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </AppStack>
          )}

          {isPreview && (
            <AppFlexbox
              style={{
                gap: 8,
                alignItems: 'center',
                marginTop: 5
              }}
            >
              <Anchor
                c="#000"
                component={Link}
                onClick={onNavigate}
                style={{ fontSize: 14, color: colorScheme.textColor }}
                to={`${baseResourceUrl}/products/${ecomStoreProduct.urlHandle}`}
              >
                View full details
              </Anchor>
              <ArrowRight color={colorScheme.textColor} size={16} />
            </AppFlexbox>
          )}

          {isCartEdit && (
            <AppFlexbox
              style={{
                flex: 1,
                gap: 10,
                marginTop: 20,
                justifyContent: 'end',
                alignItems: 'end'
              }}
            >
              {!isTabletOrSmaller && (
                <Button
                  color="dark"
                  disabled={formState.loading}
                  onClick={onEditCancel}
                  size="md"
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
              )}

              <Button
                color="blue"
                disabled={isButtonsDisabled}
                loading={formState.loading}
                size="md"
                style={{ flex: isTabletOrSmaller ? 1 : 'unset' }}
                type="submit"
                variant="filled"
              >
                Update cart
              </Button>
            </AppFlexbox>
          )}
        </AppStack>
      </AppStack>
    </AppFlexbox>
  ) : (
    <AppFlexbox
      style={{
        width: '100%',
        padding: '20px 0px',
        flexDirection: isTabletOrSmaller
          ? 'column'
          : config.desktopMediaPosition === MEDIA_POSITION_ENUM.LEFT
          ? 'row'
          : 'row-reverse'
      }}
    >
      <AppStack
        style={{
          width: '100%',
          maxWidth: isTabletOrSmaller
            ? 'unset'
            : isSmall
            ? '45%'
            : isMedium
            ? '55%'
            : '65%',
          gap: 5
        }}
      >
        <Skeleton height={isTabletOrSmaller ? 300 : 600} width="100%" />
      </AppStack>

      <AppStack
        style={{
          width: '100%',
          maxWidth: isSmall ? '55%' : isMedium ? '45%' : '35%',
          marginRight:
            isTabletOrSmaller ||
            config.desktopMediaPosition === MEDIA_POSITION_ENUM.LEFT
              ? 0
              : 50,
          marginLeft:
            isTabletOrSmaller ||
            config.desktopMediaPosition !== MEDIA_POSITION_ENUM.LEFT
              ? 0
              : 50,
          gap: 5
        }}
      >
        <AppStack style={{ gap: 8 }}>
          <Skeleton height={20} width="100%" />
          <Skeleton height={20} width="40%" />
          <Skeleton height={20} width="40%" />
          <Skeleton height={20} width="80%" />
        </AppStack>
      </AppStack>
    </AppFlexbox>
  );
};

WsProductInformationDisplay.propTypes = {
  baseStoreUrl: PropTypes.string,
  blocks: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreCartPackage: PropTypes.object,
  ecomStoreCartProduct: PropTypes.object,
  ecomStoreProduct: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  isCartEdit: PropTypes.bool,
  isPreview: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onAddOrUpdateCallback: PropTypes.func,
  onEditCancel: PropTypes.func,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  productNotFound: PropTypes.bool,
  resourceNavigation: PropTypes.array,
  selectedResources: PropTypes.object,
  themeConfig: PropTypes.object
};

export default WsProductInformationDisplay;
